<template lang="pug">
//- Plan cards, laid out as a plain responsive grid.
//-
//- This used to be a swiper carousel, which is why the subscription page was
//- commented out: `swiper/vue` is not a dependency of this project, so the
//- import failed to resolve and the component could never load. Three cards do
//- not need a carousel; a grid that wraps on narrow screens shows all of them at
//- once on desktop and stacks them on mobile, with no dependency to keep alive.
//-
//- A card lists headline figures only. With `notes` (the upgrade page), a line
//- that needs qualifying gets a numbered marker and the notes are printed under
//- the grid (code/plan-copy.ts planCardNotes); without it (the landing) the
//- lines stand alone. A billed plan's overage rates stay on its card, as short
//- lines under its "No hard stop" line.
mixin planSpecs(key)
    ul.bq-plan-specs
        li(v-for="(desc, index) in modelSpecs['" + key + "'].offer_list" :key="index")
            | {{ desc.text }}
            sup.bq-plan-mark(v-if="noteMark[desc.note]")
                a(
                    :href="'#plan-note-' + desc.note"
                    :aria-label="'Note ' + noteMark[desc.note]"
                    @click.prevent="showNote(desc.note)"
                ) {{ noteMark[desc.note] }}
            ul.bq-plan-rates(v-if="desc.rates && desc.rates.length")
                li(v-for="rate in desc.rates" :key="rate") {{ rate }}
        li.bq-plan-specs__pending(v-if="!planSpecsLoaded") Loading plan limits…

.bq-plans(:class="{ 'bq-plans--clickable': clickable }")
    .bq-plans-grid(:class="{ 'bq-plans-grid--three': showFreePlan }")
        .bq-plan-card(v-if="showFreePlan" @click="pressCard")
            .bq-plan-head
                span.bq-plan-label {{ modelSpecs['Free'].label }}
                slot(name="freeTitleExtra")
            .bq-plan-price
                span.bq-plan-amount ${{ modelSpecs['Free'].price }}
                span.bq-plan-period /mon
            .bq-plan-desc {{ modelSpecs['Free'].description }}
            .bq-plan-action
                slot(name="freeAction")
            +planSpecs('Free')

        .bq-plan-card(@click="pressCard")
            .bq-plan-head
                span.bq-plan-label {{ modelSpecs['Standard'].label }}
                slot(name="standardTitleExtra")
            .bq-plan-price
                span.bq-plan-amount ${{ modelSpecs['Standard'].price }}
                span.bq-plan-period /mon
            .bq-plan-desc {{ modelSpecs['Standard'].description }}
            .bq-plan-action
                slot(name="standardAction")
            +planSpecs('Standard')

        .bq-plan-card.bq-plan-card--accent(@click="pressCard")
            .bq-plan-head
                span.bq-plan-label {{ modelSpecs['Premium'].label }}
                slot(name="premiumTitleExtra")
            .bq-plan-price
                span.bq-plan-amount ${{ modelSpecs['Premium'].price }}
                span.bq-plan-period /mon
            .bq-plan-desc {{ modelSpecs['Premium'].description }}
            .bq-plan-action
                slot(name="premiumAction")
            +planSpecs('Premium')

    //- The footnotes. Numbered in card order, so the list numbers match the
    //- markers; each one ends with a link to its section of the docs.
    ol.bq-plan-notes(v-if="planNotes.length")
        li(
            v-for="n in planNotes"
            :key="n.id"
            :id="'plan-note-' + n.id"
            tabindex="-1"
        )
            | {{ n.text + " " }}
            a.bq-cta-link(
                v-if="n.link"
                :href="n.link.href"
                target="_blank"
                rel="noopener"
            ) {{ n.link.label }}
</template>

<script setup lang="ts">
// Descriptions, prices, labels and the derived quota lines (offer_list) all come
// from service-spec, so the cards and the plan/usage panel can never disagree.
import { modelSpecs, loadPlanSpecs, planSpecsLoaded } from "@/code/service-spec";
import { planCardNotes } from "@/code/plan-copy";
import { computed, onMounted } from "vue";

// `clickable`: the plan pages. A click anywhere on a card presses the button its
// page put in the action slot, and the cards take the landing page's card look
// (see .bq-plans--clickable below).
const props = withDefaults(defineProps<{ showFreePlan?: boolean; notes?: boolean; clickable?: boolean }>(), {
    showFreePlan: false,
    notes: false,
    clickable: false,
});

/**
 * A click on a `clickable` card presses the card's own action. A click that
 * already landed on a control (that button, a note marker, a link in the slot)
 * is left to it, a disabled action is left alone, so the card can never do what
 * its button would not, and a click that ends a text selection (the user was
 * reading, not choosing) does nothing.
 */
function pressCard(e: MouseEvent) {
    if (!props.clickable) return;
    const target = e.target as HTMLElement | null;
    if (target?.closest("a, button, input, select, textarea, label")) return;
    if (window.getSelection()?.toString()) return;
    const card = e.currentTarget as HTMLElement;
    const control = card.querySelector<HTMLElement>(".bq-plan-action button, .bq-plan-action a");
    if (!control || (control as HTMLButtonElement).disabled || control.getAttribute("aria-disabled") === "true" || control.classList.contains("disabled")) return;
    control.click();
}

// Only the notes some shown card points at, numbered in card order. Computed,
// so the notes redraw with the lines when GET /plan answers.
const planNotes = computed(() => {
    if (!props.notes) return [];
    return planCardNotes(props.showFreePlan ? ["Free", "Standard", "Premium"] : ["Standard", "Premium"]);
});

/** note id -> its marker number; an id with no note draws no marker. */
const noteMark = computed<{ [id: string]: number }>(() => {
    const marks: { [id: string]: number } = {};
    planNotes.value.forEach((n, i) => (marks[n.id] = i + 1));
    return marks;
});

/** A marker's click: bring its note into view without a router navigation. */
function showNote(id: string) {
    const el = document.getElementById(`plan-note-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
}

// Ask for the limits when the cards mount rather than relying on the module-load
// attempt alone: this view is the one that actually shows them, and an earlier
// failed attempt no longer blocks a retry.
onMounted(() => {
    void loadPlanSpecs();
});
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

/* ─── wrapper ─── */
.bq-plans {
    max-width: 1200px;
    margin: 0 auto;
    font-family: @mono;
    color: @ink;
    overflow: hidden;
    padding-top: 1rem;
}

/* ─── mobile pagination tabs ─── */
.bq-plans-pagination {
    display: none;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
}

.bq-plans-tab {
    flex: 1;
    text-align: center;
    padding: 0.6rem 0;
    font-size: 0.82rem;
    font-weight: 600;
    border: 1px solid @input-line;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;

    &:hover {
        border-color: @ink;
    }

    &.on {
        background: @btn-bg;
        color: @paper;
        border-color: @btn-bg;
    }
}

/* ─── card ─── */
.bq-plan-card {
    border: 1px solid @line;
    padding: 2rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    transition: border-color 0.15s;

    &:hover {
        border-color: @ink;
    }
}

.bq-plan-card--accent {
    border-color: @pink;
    position: relative;

    &::before {
        content: "POPULAR";
        position: absolute;
        top: -0.65rem;
        left: 1.5rem;
        padding: 0.1rem 0.55rem;
        font-size: 0.65rem;
        font-weight: 700;
        letter-spacing: 0.1em;
        background: @pink;
        color: @paper;
    }

    &:hover {
        border-color: @pink;
    }
}

/* ─── head ─── */
.bq-plan-head {
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
}

.bq-plan-label {
    font-size: 1.3rem;
    font-weight: 700;
    letter-spacing: -0.01em;
}

/* ─── price ─── */
.bq-plan-price {
    display: flex;
    align-items: baseline;
    gap: 0.35rem;
    margin-bottom: 0.75rem;
}

.bq-plan-amount {
    font-size: 2.4rem;
    font-weight: 700;
    line-height: 1;
}

.bq-plan-period {
    font-size: 0.85rem;
    color: @muted;
}

/* ─── desc ─── */
.bq-plan-desc {
    font-size: 0.82rem;
    color: @muted;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    height: calc(0.82rem * 1.6 * 3);
    overflow: hidden;
}

/* ─── action slot ─── */
.bq-plan-action {
    margin-bottom: 1.5rem;
}

/* ─── specs list ─── */
.bq-plan-specs {
    list-style: none;
    margin: 0;
    padding: 0;
    border-top: 1px solid @line;
    padding-top: 1.25rem;

    li {
        position: relative;
        padding-left: 1.4rem;
        margin-bottom: 0.6rem;
        font-size: 0.82rem;
        color: @muted;
        line-height: 1.5;

        &::before {
            content: "\2713";
            position: absolute;
            left: 0;
            font-weight: 700;
            color: @ink;
        }
    }
}

/* ─── layout ─── */
.bq-plans-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    align-items: stretch;

    &--three {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

.bq-plan-card {
    /* stretch to the tallest card in the row so the action buttons line up */
    height: 100%;
    display: flex;
    flex-direction: column;
}

// .bq-plan-specs {
//     /* push the quota list to the bottom, whatever the description's length */
//     margin-top: auto;
// }

/* A billed plan's overage rates: short lines under its "No hard stop" line,
   indented to the line's text, no check mark of their own. */
.bq-plan-rates {
    list-style: none;
    margin: 0.35rem 0 0;
    padding: 0;

    li {
        padding-left: 0;
        margin-bottom: 0.2rem;
        font-size: 0.78rem;

        &::before {
            content: none;
        }
    }
}

/* The footnote marker on a card line. */
.bq-plan-mark {
    margin-left: 0.2em;
    font-size: 0.68rem;
    line-height: 0;

    a {
        color: @ink;
        text-decoration: none;

        &:hover {
            color: @pink;
        }
    }
}

/* The footnotes under the grid (upgrade page only). */
.bq-plan-notes {
    margin: 1.5rem 0 0;
    padding: 1rem 0 0 1.4rem;
    border-top: 1px solid @line;
    font-size: 0.75rem;
    line-height: 1.6;
    color: @muted;

    li {
        margin-bottom: 0.4rem;
        scroll-margin: 5rem;

        &:focus {
            outline: none;
        }
    }
}

.bq-plan-specs__pending {
    opacity: 0.55;
    font-style: italic;

    &::before {
        content: "";
    }
}

/* ─── clickable cards (the plan pages) ─── */
/* `clickable`: a click anywhere on a card presses the button its page put in
   the action slot (pressCard), and the cards take the landing page's card
   language, so a plan reads the same wherever it is picked: paper, the 2px
   offset shadow, the lift on hover, the press while clicked, and the outlined
   button's edge and text turning brand blue with its card. The rules mirror
   the landing's .bq-pricing-cards block. A card whose action is disabled (the
   current plan, a change in flight) is not a button and does none of it. */
.bq-plans--clickable {
    /* the hover shadow of the last card in a row would be clipped */
    overflow: visible;

    .bq-plans-grid {
        gap: 1.5rem;
    }

    .bq-plan-card {
        background: @paper;
        box-shadow: 2px 2px var(--bq-shadow);
        cursor: pointer;
        transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.15s;
    }

    .bq-plan-card:has(.bq-plan-action :disabled) {
        cursor: default;
    }

    /* the lift only under a real pointer: a tap would leave a touch screen's
       card stuck raised */
    @media (hover: hover) and (pointer: fine) {
        .bq-plan-card:hover:not(:has(.bq-plan-action :disabled)) {
            transform: translate(-1px, -1px);
            box-shadow: 4px 4px var(--bq-shadow);
        }

        .bq-plan-card:hover :deep(.bq-plan-action .bq-btn--secondary:not(:disabled)) {
            border-color: @pink;
            color: @pink;
        }
    }

    .bq-plan-card:active:not(:has(.bq-plan-action :disabled)) {
        transform: translate(1px, 1px);
        box-shadow: 1px 1px var(--bq-shadow);
        transition-duration: 0.04s;
    }
}

/* ─── responsive ─── */
@media (max-width: 699px) {
    .bq-plans-grid,
    .bq-plans-grid--three {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
